import { Button, ButtonGroup, ModalBody, SmallModal } from '@george-labs.com/design-system';
import type { FC } from 'react';

import { t } from '@luna/i18n';
import { useUnsafeNavigate } from '@luna/router';
import { useNotifications } from '@shared/snackbar';

import { useDeleteBankMessage } from '../useBankMessages';

interface Props {
    isOpen: boolean;
    onClose(): void;
    overviewAbsolutePath: string;
    messageId: string | undefined;
}

export const BankMessageDeleteConfirmationModal: FC<Props> = ({
    isOpen,
    onClose,
    overviewAbsolutePath,
    messageId,
}) => {
    const navigate = useUnsafeNavigate();
    const { displaySuccess, displayError } = useNotifications('delete-bank-message');
    const { deleteById: deleteMessage, isPending: isDeletingMessage } =
        useDeleteBankMessage(messageId);

    const handleMessageDelete = async () => {
        try {
            await deleteMessage();
            navigate(overviewAbsolutePath, { absolute: true });
            displaySuccess(t('bankMessages.details.deleteMessageSuccess'));
        } catch {
            onClose();
            displayError(t('bankMessages.details.deleteMessageError'));
        }
    };

    return (
        <SmallModal
            isOpen={isOpen}
            variant={SmallModal.VARIANT.ERROR}
            title={t('bankMessages.details.deleteConfirmationTitle')}
            onClose={onClose}
            headerClassName="cyConfirmationModalHeader"
            data-cy="prompt-to-delete-a-bank-message"
            footer={
                <ButtonGroup>
                    <Button
                        data-cy="cancel-confirmation-modal-button"
                        onClick={onClose}
                        variant={Button.VARIANT.SECONDARY}
                    >
                        {t('bankMessages.details.deleteConfirmationCancelBtn')}
                    </Button>
                    <Button
                        data-cy="confirm-bank-message-deletion"
                        disabled={isDeletingMessage}
                        isLoading={isDeletingMessage}
                        onClick={handleMessageDelete}
                    >
                        {t('bankMessages.details.deleteConfirmationDeleteBtn')}
                    </Button>
                </ButtonGroup>
            }
        >
            <ModalBody>{t('bankMessages.details.deleteConfirmationText')}</ModalBody>
        </SmallModal>
    );
};
